import React, { useState } from "react";
import { Brain, FileSearch, Globe, AlertTriangle } from "lucide-react";
import { SONUC_TABS } from "./kusurConstants";
import {
    KusurKpi,
    KusurListBlock,
    KusurOkumaLogPanel,
    KusurOranBar,
    KusurTutanakPanel,
    KusurYorumlamaPanel,
} from "./KusurPanels";

export default function KusurSonucDashboard({ sonuc, sabitler, okumaLog, tutanakOnizleme, form }) {
    const [tab, setTab] = useState("ozet");

    if (!sonuc && !okumaLog && !tutanakOnizleme) {
        return (
            <div className="flex flex-col items-center justify-center h-full min-h-[280px] rounded-xl border border-dashed border-white/10 p-8 text-center text-slate-500">
                <FileSearch className="h-10 w-10 mb-3 opacity-30" />
                <p className="text-sm">Delil seçin veya yükleyin, ardından analiz başlatın.</p>
                <p className="text-[11px] mt-2 max-w-md">
                    <span className="text-sky-400">Hızlı Oku</span> → forma doldurur ·{" "}
                    <span className="text-[#a31d21]">Tam AI Analiz</span> → kusur oranı + itiraz stratejisi
                </p>
            </div>
        );
    }

    const ta = sonuc?.taraf_a || {};
    const tb = sonuc?.taraf_b || {};
    const web = sonuc?.web_arastirma || {};
    const yorum = sonuc?.kusur_yorumlama;

    return (
        <div className="flex flex-col h-full min-h-0 rounded-xl border border-white/10 bg-[#0a0f18]/80 overflow-hidden">
            <div className="flex items-center gap-1 px-2 py-1.5 border-b border-white/10 bg-[#0f1424]/80 overflow-x-auto shrink-0">
                {SONUC_TABS.map((t) => {
                    const disabled = t.id !== "ozet" && !sonuc && t.id !== "tutanak";
                    const showTutanak = t.id === "tutanak" && (sonuc?.tutanak_struct || tutanakOnizleme);
                    if (disabled && !showTutanak && t.id === "tutanak" && !tutanakOnizleme) return null;
                    return (
                        <button
                            key={t.id}
                            type="button"
                            disabled={disabled && t.id !== "tutanak"}
                            onClick={() => setTab(t.id)}
                            className={`px-2.5 py-1 rounded text-[11px] font-medium whitespace-nowrap transition ${
                                tab === t.id
                                    ? "bg-[#a31d21]/25 text-white border border-[#a31d21]/40"
                                    : "text-slate-500 hover:text-slate-300 disabled:opacity-40"
                            }`}
                        >
                            {t.label}
                        </button>
                    );
                })}
            </div>

            <div className="flex-1 overflow-y-auto p-3 space-y-3">
                {tab === "ozet" ? (
                    <>
                        {okumaLog ? <KusurOkumaLogPanel okumaLog={okumaLog} /> : null}
                        {sonuc ? (
                            <>
                                <div className="rounded-xl border border-[#a31d21]/30 bg-[#a31d21]/5 p-3">
                                    <div className="flex items-center gap-2 text-[#a31d21] text-[10px] uppercase tracking-wider mb-1">
                                        <Brain className="h-3.5 w-3.5" />
                                        MBS Akıl — {sonuc.mod} · {sonuc.delil_sayisi || 0} delil
                                    </div>
                                    <p className="text-sm text-slate-200 leading-relaxed">{sonuc.ozet}</p>
                                </div>
                                <KusurOranBar
                                    oranA={ta.kusur_orani}
                                    oranB={tb.kusur_orani}
                                    adA={ta.ad || form?.taraf_a_adi || "Müvekkil"}
                                    adB={tb.ad || form?.taraf_b_adi || "Karşı taraf"}
                                />
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                    <KusurKpi
                                        label={ta.ad || "Taraf A"}
                                        value={`%${ta.kusur_orani ?? "—"}`}
                                        accent={ta.kusur_orani === 0 ? "emerald" : "red"}
                                        compact
                                    />
                                    <KusurKpi label={tb.ad || "Taraf B"} value={`%${tb.kusur_orani ?? "—"}`} accent="red" compact />
                                    <KusurKpi
                                        label="Güven"
                                        value={sonuc.guven_skoru != null ? `${sonuc.guven_skoru}/100` : "—"}
                                        compact
                                    />
                                    <KusurKpi label="Web" value={web.kaynaklar?.length ?? 0} hint="Kaynak" accent="sky" compact />
                                </div>
                                {form?.mevcut_kusur_orani !== "" && form?.mevcut_kusur_orani != null ? (
                                    <div className="rounded-lg border border-amber-500/25 bg-amber-500/5 p-2 text-[11px] text-amber-100/90">
                                        Dosyadaki mevcut kusur: <strong>%{form.mevcut_kusur_orani}</strong>
                                        {yorum?.oran_etiket_analiz ? (
                                            <span className="text-slate-400 ml-2">→ Analiz: {yorum.oran_etiket_analiz}</span>
                                        ) : null}
                                    </div>
                                ) : null}
                                {yorum ? <KusurYorumlamaPanel yorum={yorum} /> : null}
                                {sonuc.mbs_firma_degerlendirmesi ? (
                                    <div className="rounded-lg border border-white/10 p-3 text-[11px] text-slate-300 whitespace-pre-wrap">
                                        {sonuc.mbs_firma_degerlendirmesi}
                                    </div>
                                ) : null}
                            </>
                        ) : tutanakOnizleme ? (
                            <KusurTutanakPanel ts={tutanakOnizleme} compact />
                        ) : null}
                    </>
                ) : null}

                {tab === "tutanak" ? (
                    <KusurTutanakPanel ts={sonuc?.tutanak_struct || tutanakOnizleme} />
                ) : null}

                {tab === "deliller" && sonuc ? (
                    <div className="grid md:grid-cols-2 gap-2">
                        <KusurListBlock title="Delil Analizi" items={sonuc.delil_analizi} />
                        <KusurListBlock title="KTK m.84 İhlalleri" items={sonuc.ktk_ihlalleri} />
                        <KusurListBlock title="Eksik Deliller" items={sonuc.eksik_deliller} />
                        <KusurListBlock title="Riskler" items={sonuc.riskler} />
                        {sonuc.duzeltme_notu ? (
                            <div className="md:col-span-2 rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-2 text-[11px] text-emerald-100">
                                <strong>Tutanak düzeltmesi:</strong> {sonuc.duzeltme_notu}
                            </div>
                        ) : null}
                    </div>
                ) : null}

                {tab === "itiraz" && sonuc ? (
                    <div className="space-y-3">
                        <KusurListBlock title="İtiraz Önerileri" items={sonuc.itiraz_onerileri} />
                        <KusurListBlock title="MBS Hukuk Önerileri" items={sonuc.mbs_onerileri} />
                        {sonuc.sbm_itiraz_taslak ? (
                            <div className="rounded-lg border border-violet-500/25 bg-violet-500/5 p-3">
                                <div className="text-[10px] uppercase text-violet-300 mb-1">SBM İtiraz Taslağı</div>
                                <p className="text-[11px] text-slate-300 whitespace-pre-wrap leading-relaxed">
                                    {sonuc.sbm_itiraz_taslak}
                                </p>
                            </div>
                        ) : null}
                        {sabitler?.itiraz_hatirlatma ? (
                            <p className="text-[10px] text-slate-500">{sabitler.itiraz_hatirlatma}</p>
                        ) : null}
                    </div>
                ) : null}

                {tab === "strateji" && sonuc ? (
                    <div className="space-y-3">
                        {web.kaynaklar?.length ? (
                            <div className="rounded-lg border border-sky-500/20 bg-sky-500/5 p-3 text-[11px]">
                                <div className="flex items-center gap-1.5 text-sky-300 mb-1">
                                    <Globe className="h-3.5 w-3.5" />
                                    {web.kaynaklar.length} web kaynağı
                                </div>
                                {web.ozet_not ? <p className="text-slate-300">{web.ozet_not}</p> : null}
                            </div>
                        ) : null}
                        {sonuc.tahmin ? (
                            <div className="rounded-xl border border-violet-500/25 bg-violet-500/5 p-3 space-y-2 text-[11px] text-slate-300">
                                <div className="text-sm font-medium text-violet-200">AI Tahmin & Strateji</div>
                                {sonuc.tahmin.kusur_senaryo ? (
                                    <div>
                                        <span className="text-slate-500">Senaryo:</span> {sonuc.tahmin.kusur_senaryo}
                                    </div>
                                ) : null}
                                {sonuc.tahmin.itiraz_basarisi ? (
                                    <div>
                                        <span className="text-slate-500">İtiraz başarısı:</span>{" "}
                                        {sonuc.tahmin.itiraz_basarisi}
                                    </div>
                                ) : null}
                                {sonuc.tahmin.risk_puani != null ? (
                                    <div>
                                        <span className="text-slate-500">Risk:</span> {sonuc.tahmin.risk_puani}/100
                                    </div>
                                ) : null}
                                {sonuc.tahmin.oneri_strateji ? (
                                    <div className="border-t border-white/10 pt-2">{sonuc.tahmin.oneri_strateji}</div>
                                ) : null}
                            </div>
                        ) : null}
                        {sonuc.yasal_uyari ? (
                            <div className="flex gap-2 text-[11px] text-amber-200/90 bg-amber-500/10 border border-amber-500/20 rounded-lg p-2">
                                <AlertTriangle className="h-4 w-4 shrink-0" />
                                {sonuc.yasal_uyari}
                            </div>
                        ) : null}
                    </div>
                ) : null}
            </div>
        </div>
    );
}
